// components/ContributorMemoryFeed.jsx — Anamoria SPA
// v1.10 — NEW-163 Increment B (2026-10-30): RESPONSE CARD. A memory that answers the leader's note
//   (row carries groupNoteId — anamoria-contributors v1.39) renders with the GroupMemoryCard header
//   (avatar, name, "remembering {name}", MediaTypeChip, day) + pill at the top of its existing card
//   body, iff isResponseCard(memory, spaceIsGroup) — i.e. groupNoteId && spaceIsGroup (never feed-wide,
//   never in a private space). On a response card the footer keeps only the sharing-state pill (name
//   and day already sit in the header). New props spaceIsGroup (default false) + groupNotes (for the
//   pill); without them the feed is byte-identical to v1.9. Spec v0.2 §4; mockup v0.8.
// v1.9 — Feed video-refresh (2026-09-28): the memories list previously fetched once on
//   mount and never again, so a video's thumbnail stayed on its loading skeleton forever
//   once postprocess finished, until a manual page reload. Adds (a) a silent refetch on
//   window focus / tab visibility, and (b) a bounded, backing-off poll (isVideoPending,
//   POLL_SCHEDULE_MS, POLL_CAP_MS — all exported for test coverage) that only runs while
//   a recent video memory has no videoNote yet, paused while the tab is hidden, capped at
//   POLL_CAP_MS. No backend change — reuses the existing GET {prefix}/{spaceId}/memories
//   route. Mirrors the equivalent owner-side change in MemoryFeed.jsx, adjusted there for
//   that component's module-level memories cache (see its own changelog for why the two
//   aren't identical). Plan: Anamoria_ContributorFeed_VideoRefresh_BUILD_Plan_v0.2.md.
// v1.8 — NEW-65 (PR-7b): tap a SHARED memory the contributor did NOT author to open the
//   read-only detail (onViewMemory). Accessible per WCAG 2.2 / WAI-ARIA APG: role=button,
//   tabIndex, Enter/Space keydown, aria-label, visible focus ring. Own cards keep pencil-only
//   (no body tap) — mutually exclusive with the tap target, so no nested interactive elements.
// v1.7 — NEW-73 (PR-8): read-only sharing-state pill on the contributor's OWN cards only.
//   Own card shows who can currently see the memory, from server visibility_pref + review_status
//   (never inferred): 'Only you' / 'Shared with the space owner' / 'Shared with the group' /
//   (group+pending) 'Shared with the space owner · not yet with the group' (responsive trim on
//   narrow screens keeps the second half). group+rejected shows silently as 'Shared with the space
//   owner' (Copy v0.4 §7, judgment B). Copy verbatim from Anamoria_ContributorMemory_Visibility_Copy_v0.4.
//   Read-only display; no controls, no navigation, no backend/network change. Self-contained inline
//   styles + a scoped <style> for the responsive trim (shared MemoryFeed.module.css untouched, BL-1-safe).
// v1.6 — consent prompt visual refinement: dashed sage border moves to the card div itself
//   (not the outer masonryItem wrapper); consent prompt marginTop removed so it sits flush
//   directly below the card, connected without a gap.
// v1.5 — consent prompt visual: dashed sage border on the masonryItem wrapper when
//   memory.isOwn && memory.shareRequested, so the prompt reads as belonging to the card above it.
// v1.4 — NEW-19/copy fix: add ownerName prop (default 'The space owner'); renderConsentPrompt now
//   uses thanatologist-approved copy: "{ownerName} would like to share this memory with everyone in
//   the group. It's your memory, and whatever you choose is okay." Replaces the "The family"
//   placeholder. ownerName is provided by ContributorFeedPage from the handleContributorSpace
//   response (anamoria-contributors v1.22). Dark.
// v1.3 — NEW-3: consent prompt on the contributor's OWN leader-only piece with an open share
//   request (memory.isOwn && memory.shareRequested). "Share with the group" / "Keep it just between
//   us" -> POST {prefix}/{spaceId}/memories/{id}/share-response {agree} (Endpoint B, optimistic).
//   Dark-by-data (shareRequested is set only by Endpoint A under group_spaces_enabled). (NEW3 frontend plan v0.2, PR2)
// v1.2 — Account-link access mode: prefix prop (May 31, 2026)
//
// Changes from v1.1:
//   - Added `prefix` prop (default '/contribute' → no regression in session
//     mode). The memories fetch now uses `${prefix}/${spaceId}/memories` so the
//     same component serves both the session route ('/contribute') and the
//     linked route ('/me/contributions'). The caller (ContributorFeedPage)
//     supplies the prefix matching its access mode.
//   - The /media/playback/:key call is intentionally NOT prefixed — it is a
//     shared media route that accepts BOTH the contributor session token and
//     the owner JWT (the playback handler checks both), so it is correct as-is
//     in either mode.
//
// v1.1 — Contributor edit icon on own memories (May 25, 2026)
//
// Changes from v1.0:
//   - Added onEditMemory prop — callback invoked when contributor taps
//     edit icon on a memory they created (isOwn === true).
//   - Edit pencil icon overlay rendered on cards where memory.isOwn is true.
//     Other contributors' memories remain read-only (no icon).
//   - Uses same hover overlay pattern as owner MemoryFeed.jsx.
//
// v1.0 — Session 3 (April 19, 2026)
//
// Contributor-side masonry feed. Parallel implementation to MemoryFeed.jsx v2.1
// per Session 3 Plan v1.1 decisions A3 (new component, reuse MemoryFeed.module.css)
// and V (contributors see each other's memories only).
//
// Reuses MemoryFeed.module.css for all layout and card styling. No new CSS.
//
// Differences from owner MemoryFeed v2.1:
//   - Single view (no Private/Shared tabs) — decision V
//   - No favorite heart overlay
//   - No edit pencil overlay — decision E2 (edit deferred to Session 3.5)
//   - No MemoryDetailPage navigation — contributors have no detail page
//   - No privacy icon in card footer — contributor memories are all non-private
//   - Card footer shows creator attribution "{Name} {M/D/YY}" — decision CN
//   - Voice cards render inline as metadata only (no play button) — decision V
//     Contributors can see voice memories exist but not play them.
//   - Photo cards still fetch signed URLs via /media/playback/:key — PF-10 verified
//     this route accepts contributor session tokens.
//
// Props:
//   spaceId       — space UUID
//   getApi        — factory returning contributor API client (from createContributorApiClient)
//   onMemoryCount — optional callback invoked with memory count after load
//
// Masonry behavior, Macy.js loader, and photo URL fetch pattern all mirror
// MemoryFeed v2.1 exactly — only the card rendering and feature set differ.

import { useState, useEffect, useRef, useCallback } from 'react';
import GroupMemoryCard, { isResponseCard, ResponseDay, ResponseChip } from './GroupMemoryCard';   // v1.10 (NEW-163)
import styles from './MemoryFeed.module.css';

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */

// Attribution date format per decision CN: short M/D/YY.
// Example: "4/19/26" for April 19, 2026.
function formatShortDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });
  } catch {
    return '';
  }
}

// Voice duration MM:SS (matches VoiceCard.formatDuration)
function formatDuration(val) {
  const s = Math.round(Number(val) || 0);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

// Waveform bar heights — same 12-bar pattern as owner VoiceCard for visual consistency.
const WAVE_BARS = [8, 14, 10, 18, 12, 20, 8, 16, 14, 10, 18, 6];

/* v1.1: Edit pencil overlay — shown on contributor's own memories only.
   Inline styles to avoid modifying owner's MemoryFeed.module.css. */
function EditOverlay({ memory, onEdit }) {
  if (!memory.isOwn || !onEdit) return null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onEdit(memory); }}
      aria-label="Edit memory"
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.9)',
        border: '1px solid var(--color-border-light, #edebe7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: 0,
        color: 'var(--color-sage-dark, #5b7a65)',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );
}

/* ═══════════════════════════════════════
   MACY.JS DYNAMIC LOADER
   Duplicated from MemoryFeed.jsx per Cap-1 isolation principle.
   If this loader changes there, update here too (coupling documented).
   ═══════════════════════════════════════ */

let macyPromise = null;

function loadMacy() {
  if (macyPromise) return macyPromise;
  macyPromise = new Promise((resolve, reject) => {
    if (window.Macy) {
      resolve(window.Macy);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/macy@2/dist/macy.js';
    script.async = true;
    script.onload = () => resolve(window.Macy);
    script.onerror = () => {
      macyPromise = null;
      reject(new Error('Failed to load Macy.js'));
    };
    document.head.appendChild(script);
  });
  return macyPromise;
}

/* ═══════════════════════════════════════
   CONTRIBUTOR MEMORY FEED COMPONENT
   ═══════════════════════════════════════ */

// v1.7 (NEW-73): map a contributor's OWN memory to its sharing-state pill.
// Copy verbatim from Anamoria_ContributorMemory_Visibility_Copy_v0.4 §7. Returns null on a memory
// that is not the contributor's own, or when the tier is unknown (omit rather than mislabel).
export function pillFor(memory) {
  if (!memory || !memory.isOwn) return null;
  const vp = memory.visibilityPref;
  const rs = memory.reviewStatus;
  if (vp === 'contributor_only') return { kind: 'simple', text: 'Only you' };
  if (vp === 'owner_contributor_only') return { kind: 'simple', text: 'Shared with the space owner' };
  if (vp === 'group') {
    if (rs === 'approved') return { kind: 'simple', text: 'Shared with the group' };
    if (rs === 'pending') return { kind: 'pending' };
    // rejected (and any non-approved/non-pending): silently the same as owner-shared (Copy v0.4 §7, judgment B)
    return { kind: 'simple', text: 'Shared with the space owner' };
  }
  return null;
}

// v1.8 (NEW-65): pure tap-target contract — is this card tappable-to-view, and its aria-label?
// Own memories are never tappable (pencil-only, no body tap). Exported for contract-lock testing.
export function tapTargetFor(memory, category, hasView) {
  if (!memory || memory.isOwn || !hasView) return { tappable: false, ariaLabel: null };
  return {
    tappable: true,
    ariaLabel: `View ${memory.title || category + ' memory'}${memory.creatorName ? ', shared by ' + memory.creatorName : ''}`,
  };
}

// v1.9 (feed video-refresh, 2026-09-28): a video memory is "still processing" when
// it has no videoNote yet (server only populates videoNote once playback_key exists —
// see handleContributorFeed) and it's recent enough that processing genuinely still
// in flight is the likely explanation (older un-processed videos are left alone —
// falls back to today's manual-refresh behavior rather than polling forever).
// Exported for test coverage, matching the pillFor/tapTargetFor contract-lock pattern.
export const PENDING_VIDEO_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
export const POLL_SCHEDULE_MS = [5000, 5000, 8000, 13000]; // repeats the last value once exhausted
export const POLL_CAP_MS = 90 * 1000; // give up polling after this long since pending was first observed
export function isVideoPending(memory) {
  if (!memory || (memory.category || '').toLowerCase() !== 'video') return false;
  if (memory.videoNote) return false;
  const created = new Date(memory.createdAt).getTime();
  if (!created || Number.isNaN(created)) return false;
  return Date.now() - created < PENDING_VIDEO_WINDOW_MS;
}

const STATUS_PILL_BASE = {
  maxWidth: '72%', textAlign: 'right', whiteSpace: 'normal',
  fontSize: '10px', fontWeight: 600, lineHeight: 1.35,
  padding: '2px 8px', borderRadius: '999px',
  background: 'var(--color-cream-2, #EFEADF)', color: '#6b6459',
  fontFamily: "var(--font-sans, 'DM Sans', -apple-system, sans-serif)",
};

export default function ContributorMemoryFeed({ spaceId, getApi, prefix = '/me/contributions', onMemoryCount, onEditMemory, onViewMemory, ownerName = 'The space owner', spaceIsGroup = false, groupNotes = null }) {
  const masonryRef = useRef(null);
  const macyInstanceRef = useRef(null);

  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photoUrls, setPhotoUrls] = useState({}); // s3Key → signed URL
  const [videoPosterUrls, setVideoPosterUrls] = useState({}); // posterKey → signed URL (NEW-121)
  const [macyReady, setMacyReady] = useState(false);
  const [respondingId, setRespondingId] = useState(null); // NEW-3: memory id currently answering a share request

  /* ─── Fetch contributor memories ───
     v1.9 (feed video-refresh, 2026-09-28): loadMemories is now a reusable function
     (not just an effect-local closure) so it can also be called from the
     focus/visibility listener and the bounded poll below — same request, same
     endpoint, nothing about the mount-time load itself changed. `silent` skips the
     full-page loading spinner and error banner for a background refresh so a
     transient poll failure doesn't clobber a feed that's already rendering fine.
     requestIdRef supersedes the old per-effect `cancelled` closure: calling
     loadMemories() bumps the id, so a stale in-flight response (e.g. spaceId
     changed mid-request) is discarded by the id check below — same protection,
     now shared across all three call sites instead of just the mount effect. */
  const requestIdRef = useRef(0);

  const loadMemories = useCallback(async ({ silent = false } = {}) => {
    const myRequestId = ++requestIdRef.current;
    if (!silent) setLoading(true);
    try {
      const api = getApi();
      const data = await api.get(`${prefix}/${spaceId}/memories?limit=100`);
      if (requestIdRef.current !== myRequestId) return; // superseded — discard
      const mems = data.memories || [];
      setMemories(mems);
      if (onMemoryCount) onMemoryCount(mems.length);
      if (!silent) setError('');
    } catch (err) {
      if (requestIdRef.current === myRequestId && !silent) setError('Could not load memories.');
    } finally {
      if (requestIdRef.current === myRequestId && !silent) setLoading(false);
    }
  }, [getApi, prefix, spaceId, onMemoryCount]);

  useEffect(() => {
    loadMemories();
  }, [loadMemories]);

  /* ─── Refetch on focus/visibility (v1.9, feed video-refresh) ───
     Free baseline fix for "switched away and came back, thumbnail still blank":
     unconditional silent refetch, no cache to protect on this side (contrast
     with the owner-side MemoryFeed.jsx, which gates this — see that file's
     comment for why). */
  useEffect(() => {
    function handleVisibility() {
      if (!document.hidden) loadMemories({ silent: true });
    }
    function handleFocus() {
      loadMemories({ silent: true });
    }
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadMemories]);

  /* ─── Bounded, backing-off poll while a video is still processing (v1.9) ───
     Self-driving: re-evaluates on every `memories` change (including its own
     silent refetches), so it naturally stops the moment isVideoPending goes
     false, and re-schedules itself otherwise. Paused while the tab is hidden
     (resumes via the visibility listener above); gives up after POLL_CAP_MS
     from when pending was first observed, falling back to today's
     manual-refresh behavior rather than polling silently forever. */
  const pollTimerRef = useRef(null);
  const pollStepRef = useRef(0);
  const pollStartedAtRef = useRef(null);

  useEffect(() => {
    const clearPoll = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const pending = memories.some(isVideoPending);

    if (!pending) {
      clearPoll();
      pollStepRef.current = 0;
      pollStartedAtRef.current = null;
      return undefined;
    }

    if (document.hidden) {
      clearPoll();
      return undefined;
    }

    if (pollStartedAtRef.current === null) pollStartedAtRef.current = Date.now();
    if (Date.now() - pollStartedAtRef.current > POLL_CAP_MS) {
      clearPoll();
      return undefined;
    }

    const step = Math.min(pollStepRef.current, POLL_SCHEDULE_MS.length - 1);
    pollTimerRef.current = setTimeout(() => {
      pollStepRef.current += 1;
      loadMemories({ silent: true });
    }, POLL_SCHEDULE_MS[step]);

    return clearPoll;
  }, [memories, loadMemories]);

  /* ─── Fetch photo signed URLs ───
     Uses existing route GET /media/playback/:key. PF-10 empirically verified
     this route accepts contributor session tokens. See Session 3 Plan v1.1
     Addendum §3.2. */
  const fetchedKeysRef = useRef(new Set());

  useEffect(() => {
    if (memories.length === 0) return;
    const photoMems = memories.filter(
      (m) => (m.category || '').toLowerCase() === 'photo'
        && m.s3Key
        && !fetchedKeysRef.current.has(m.s3Key)
    );
    if (photoMems.length === 0) return;

    photoMems.forEach((m) => fetchedKeysRef.current.add(m.s3Key));

    let cancelled = false;

    async function fetchPhotoUrls() {
      const api = getApi();
      const results = {};

      const chunks = [];
      for (let i = 0; i < photoMems.length; i += 6) {
        chunks.push(photoMems.slice(i, i + 6));
      }

      for (const chunk of chunks) {
        if (cancelled) break;
        const promises = chunk.map(async (m) => {
          try {
            const data = await api.get(`/media/playback/${encodeURIComponent(m.s3Key)}`);
            results[m.s3Key] = data.playbackUrl;
          } catch (err) {
            console.error('Photo URL fetch error:', err);
            fetchedKeysRef.current.delete(m.s3Key);
          }
        });
        await Promise.all(promises);
      }

      if (!cancelled && Object.keys(results).length > 0) {
        setPhotoUrls((prev) => ({ ...prev, ...results }));
      }
    }

    fetchPhotoUrls();
    return () => { cancelled = true; };
  }, [memories, getApi]);

  /* ─── NEW-121: fetch video poster signed URLs ───
     Same route + batching pattern as the photo fetch above. Only the poster
     is needed here (a static grid thumbnail) — full playback is handled by
     ContributorMemoryDetailPage.jsx, which fetches playbackKey itself when a
     shared video is opened. Own videos freshly created won't have a poster
     yet (async transcode pipeline hasn't run) — the render branch below
     falls back to a skeleton in that case, same treatment as a loading photo. */
  const fetchedPosterKeysRef = useRef(new Set());

  useEffect(() => {
    if (memories.length === 0) return;
    const videoMems = memories.filter(
      (m) => (m.category || '').toLowerCase() === 'video'
        && m.videoNote?.posterKey
        && !fetchedPosterKeysRef.current.has(m.videoNote.posterKey)
    );
    if (videoMems.length === 0) return;

    videoMems.forEach((m) => fetchedPosterKeysRef.current.add(m.videoNote.posterKey));

    let cancelled = false;

    async function fetchVideoPosterUrls() {
      const api = getApi();
      const results = {};

      const chunks = [];
      for (let i = 0; i < videoMems.length; i += 6) {
        chunks.push(videoMems.slice(i, i + 6));
      }

      for (const chunk of chunks) {
        if (cancelled) break;
        const promises = chunk.map(async (m) => {
          const key = m.videoNote.posterKey;
          try {
            const data = await api.get(`/media/playback/${encodeURIComponent(key)}`);
            results[key] = data.playbackUrl;
          } catch (err) {
            console.error('Video poster URL fetch error:', err);
            fetchedPosterKeysRef.current.delete(key);
          }
        });
        await Promise.all(promises);
      }

      if (!cancelled && Object.keys(results).length > 0) {
        setVideoPosterUrls((prev) => ({ ...prev, ...results }));
      }
    }

    fetchVideoPosterUrls();
    return () => { cancelled = true; };
  }, [memories, getApi]);

  /* ─── Initialize Macy.js ─── */
  useEffect(() => {
    let instance = null;
    let cancelled = false;

    loadMacy()
      .then((Macy) => {
        if (cancelled || !masonryRef.current) return;

        instance = Macy({
          container: masonryRef.current,
          trueOrder: false,
          waitForImages: true,
          margin: 16,
          columns: 5,
          breakAt: {
            1279: 4,
            899: 3,
            599: 2,
          },
        });

        macyInstanceRef.current = instance;

        if (instance.on) {
          instance.on(instance.constants.EVENT_IMAGE_COMPLETE, () => {
            instance.recalculate(true, true);
          });
        }

        if (!cancelled) setMacyReady(true);
      })
      .catch(() => {
        if (!cancelled) setMacyReady(false);
      });

    return () => {
      cancelled = true;
      if (instance) {
        try { instance.remove(); } catch { /* ignore */ }
      }
      macyInstanceRef.current = null;
    };
  }, []);

  /* ─── Recalculate Macy when data changes ─── */
  useEffect(() => {
    const inst = macyInstanceRef.current;
    if (!inst) return;

    const raf1 = requestAnimationFrame(() => {
      inst.recalculate(true, true);
      const raf2 = requestAnimationFrame(() => {
        inst.recalculate(true, true);
      });
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [memories, photoUrls, videoPosterUrls]);

  /* ─── Recalculate on photo load ─── */
  const handleImageLoad = useCallback(() => {
    const inst = macyInstanceRef.current;
    if (inst) {
      requestAnimationFrame(() => inst.recalculate(true, true));
    }
  }, []);

  /* ─── Render states ─── */

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className="app-loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
      </div>
    );
  }

  /* ─── NEW-3: consent answer to a leader's "Ask to share this" (optimistic).
     POST {prefix}/{spaceId}/memories/{id}/share-response {agree}. Only the piece's own author sees
     the prompt (memory.isOwn + memory.shareRequested — set only by Endpoint A under group spaces,
     so this is dark-by-data). Copy PLACEHOLDER pending thanatologist/brand-voice. ─── */
  const handleShareResponse = async (memory, agree) => {
    setRespondingId(memory.id);
    setMemories((prev) => prev.map((m) => (m.id === memory.id
      ? { ...m, shareRequested: false, ...(agree ? { visibilityPref: 'group', reviewStatus: 'approved' } : {}) }
      : m)));
    try {
      const api = getApi();
      await api.post(`${prefix}/${spaceId}/memories/${memory.id}/share-response`, { agree });
    } catch (err) {
      console.error('Share response failed:', err);
      setMemories((prev) => prev.map((m) => (m.id === memory.id ? { ...m, shareRequested: true } : m))); // rollback the prompt
    } finally {
      setRespondingId(null);
    }
  };

  const renderConsentPrompt = (memory) => {
    if (!memory.isOwn || !memory.shareRequested) return null;
    const busy = respondingId === memory.id;
    return (
      <div style={{ padding: '10px 12px', background: '#F5F1E7', borderRadius: '10px' }}>
        <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: '#4a4a4a', lineHeight: 1.4 }}>
          {ownerName} would like to share this memory with everyone in the group. It's your memory, and whatever you choose is okay.
        </p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); handleShareResponse(memory, false); }}
            style={{ background: 'none', border: 'none', color: '#737373', fontSize: '0.82rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}
          >
            Keep it just between us
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); handleShareResponse(memory, true); }}
            style={{ background: 'none', border: 'none', color: '#838B70', fontSize: '0.82rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}
          >
            Share with the group
          </button>
        </div>
      </div>
    );
  };

  // v1.7 (NEW-73): read-only sharing-state pill, own cards only. Placed as the second child of the
  // flex space-between cardFooter → sits right-aligned opposite the date; null renders nothing.
  const renderStatusPill = (memory) => {
    const p = pillFor(memory);
    if (!p) return null;
    if (p.kind === 'simple') {
      return <span className="cmf-pill" style={STATUS_PILL_BASE}>{p.text}</span>;
    }
    // pending — responsive: trim the state half on narrow screens; always keep '· not yet with the group'
    return (
      <span className="cmf-pill" style={STATUS_PILL_BASE}>
        <span className="cmf-pill-full">Shared with the space owner</span>
        <span className="cmf-pill-trim">With the space owner</span>
        {'\u00A0\u00B7\u00A0not yet with the group'}
      </span>
    );
  };

  // Empty state — no contributor memories yet
  if (memories.length === 0) {
    const spaceName = sessionStorage.getItem('ana_spaceName') || 'this space';
    return (
      <div className={styles.empty}>
        <span className={styles.emptyIcon}>💭</span>
        <p className={styles.emptyTitle}>No memories yet</p>
        <p className={styles.emptyText}>
          Be the first to share a memory of {spaceName}.
        </p>
      </div>
    );
  }

  /* v1.8 NEW-65: tap-to-view attrs for a SHARED memory the contributor did NOT author.
     Own cards get none (pencil-only). Semantic button role + keyboard + label per WCAG 2.2. */
  const tapAttrs = (memory, category) => {
    const t = tapTargetFor(memory, category, !!onViewMemory);
    if (!t.tappable) return { cls: '', props: {} };
    return {
      cls: ' cmf-tappable',
      props: {
        role: 'button',
        tabIndex: 0,
        onClick: () => onViewMemory(memory),
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewMemory(memory); } },
        'aria-label': t.ariaLabel,
      },
    };
  };

  return (
    <div className={styles.feedContainer}>
      <style>{`.cmf-pill-trim{display:none}@media (max-width:599px){.cmf-pill-full{display:none}.cmf-pill-trim{display:inline}}.cmf-tappable{cursor:pointer}.cmf-tappable:focus-visible{outline:2px solid #838B70;outline-offset:2px;border-radius:12px}`}</style>
      <div
        ref={masonryRef}
        className={`${styles.masonry} ${!macyReady ? styles.masonryFallback : ''}`}
      >
        {memories.map((memory) => {
          const category = (memory.category || '').toLowerCase();
          const attribution = memory.creatorName
            ? `${memory.creatorName} ${formatShortDate(memory.createdAt)}`
            : formatShortDate(memory.createdAt);
          const tap = tapAttrs(memory, category);
          // v1.10 (NEW-163): response-card treatment iff groupNoteId && spaceIsGroup.
          const respond = isResponseCard(memory, spaceIsGroup);
          const responseHeader = respond ? <GroupMemoryCard memory={memory} groupNotes={groupNotes} leaderName={ownerName} insetRight={memory.isOwn && onEditMemory ? 36 : 0} /> : null;   // keep clear of the own-card pencil
          const footerDate = respond ? <ResponseDay memory={memory} /> : attribution;   // response card: "Today" on the left (name is in the header)
          const footerEnd = respond   // response card: type chip just before the sharing-state pill
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ResponseChip memory={memory} category={category} />{renderStatusPill(memory)}</span>
            : renderStatusPill(memory);

          /* ── Voice card (metadata only — no play button per decision V) ── */
          if (category === 'voice') {
            const duration = memory.voiceNote?.duration || 0;
            return (
              <div key={memory.id} className={styles.masonryItem}>
                <div className={`${styles.textCard}${tap.cls}`} {...tap.props} style={{position:'relative', ...(tap.cls ? { cursor: 'pointer' } : {}), ...(memory.isOwn && memory.shareRequested ? { border: '2px dashed #838B70' } : {})}}>
                  <EditOverlay memory={memory} onEdit={onEditMemory} />
                  {responseHeader}
                  {!respond && <p className={styles.textLabel}>VOICE</p>}   {/* v1.10: the response header's chip already says it */}
                  <div className={styles.textContent}>
                    {memory.title && (
                      <p className={styles.textTitle}>{memory.title}</p>
                    )}
                    {/* Static waveform + duration — visual indicator only */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '8px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          flex: 1,
                        }}
                      >
                        {WAVE_BARS.map((h, i) => (
                          <div
                            key={i}
                            style={{
                              width: '2px',
                              height: `${h}px`,
                              background: 'var(--color-sage-dark, #5b7a65)',
                              opacity: 0.3,
                              borderRadius: '1px',
                            }}
                          />
                        ))}
                      </div>
                      <span
                        style={{
                          fontSize: '11px',
                          color: 'var(--color-text-subtle, #737373)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatDuration(duration)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <span className={styles.cardDate}>{footerDate}</span>{footerEnd}
                  </div>
                </div>
                {renderConsentPrompt(memory)}
              </div>
            );
          }

          /* ── Photo card ── */
          if (category === 'photo') {
            const photoUrl = photoUrls[memory.s3Key];
            return (
              <div key={memory.id} className={`${styles.masonryItem} ${styles.photoItem}`}>
                <div className={`${styles.photoCard}${tap.cls}`} {...tap.props} style={{position:'relative', ...(tap.cls ? { cursor: 'pointer' } : {}), ...(memory.isOwn && memory.shareRequested ? { border: '2px dashed #838B70' } : {})}}>
                  <EditOverlay memory={memory} onEdit={onEditMemory} />
                  {responseHeader}
                  <div className={styles.photoImageWrap}>
                    {photoUrl ? (
                      <img
                        className={styles.photoImage}
                        src={photoUrl}
                        alt={memory.title || 'Photo memory'}
                        onLoad={handleImageLoad}
                      />
                    ) : (
                      <div className={styles.photoSkeleton}>
                        <div className={styles.photoSkeletonShimmer} />
                      </div>
                    )}
                  </div>

                  <div className={styles.photoContent}>
                    {memory.title && (
                      <p className={styles.photoTitle}>{memory.title}</p>
                    )}
                    {!memory.title && <p className={styles.photoLabel}>Photo</p>}
                  </div>

                  <div className={`${styles.cardFooter} ${styles.photoCardFooter}`}>
                    <span className={styles.cardDate}>{footerDate}</span>{footerEnd}
                  </div>
                </div>
                {renderConsentPrompt(memory)}
              </div>
            );
          }

          /* ── Video card (NEW-121) — simple preview: poster/skeleton + label + duration.
             Matches the voice card's deliberate simplicity (metadata-only in the grid);
             full playback lives in ContributorMemoryDetailPage.jsx, reached via tap for a
             shared-by-others video. Own videos get no edit affordance (v1 is create-only —
             see plan v0.4 §1a). No VideoCard.jsx reuse — that component is owner-feed-only. ── */
          if (category === 'video') {
            const posterKey = memory.videoNote?.posterKey;
            const posterUrl = posterKey ? videoPosterUrls[posterKey] : null;
            const duration = memory.videoNote?.duration || 0;
            return (
              <div key={memory.id} className={`${styles.masonryItem} ${styles.photoItem}`}>
                <div className={`${styles.photoCard}${tap.cls}`} {...tap.props} style={{position:'relative', ...(tap.cls ? { cursor: 'pointer' } : {}), ...(memory.isOwn && memory.shareRequested ? { border: '2px dashed #838B70' } : {})}}>
                  {/* NEW-128: video was create-only in v1 (NEW-121) -- no edit
                      affordance existed. Adding it here, matching the
                      voice/photo pattern exactly, now that edit/re-record
                      exists for video (ContributorVideoPage.jsx). */}
                  <EditOverlay memory={memory} onEdit={onEditMemory} />
                  {responseHeader}
                  <div className={styles.photoImageWrap}>
                    {posterUrl ? (
                      <img
                        className={styles.photoImage}
                        src={posterUrl}
                        alt={memory.title || 'Video memory'}
                        onLoad={handleImageLoad}
                      />
                    ) : (
                      <div className={styles.photoSkeleton}>
                        <div className={styles.photoSkeletonShimmer} />
                      </div>
                    )}
                    {/* Play glyph + duration badge, bottom-right — poster-only, no inline playback here */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute', bottom: '8px', right: '8px',
                        display: 'flex', alignItems: 'center', gap: '4px',
                        background: 'rgba(0,0,0,0.6)', color: '#fff',
                        borderRadius: '999px', padding: '3px 8px',
                        fontSize: '11px', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      <svg width="9" height="10" viewBox="0 0 12 14" fill="white"><path d="M0 0l12 7-12 7V0z" /></svg>
                      {duration > 0 && <span>{formatDuration(duration)}</span>}
                    </div>
                  </div>

                  <div className={styles.photoContent}>
                    {memory.title && (
                      <p className={styles.photoTitle}>{memory.title}</p>
                    )}
                    {!memory.title && <p className={styles.photoLabel}>Video</p>}
                  </div>

                  <div className={`${styles.cardFooter} ${styles.photoCardFooter}`}>
                    <span className={styles.cardDate}>{footerDate}</span>{footerEnd}
                  </div>
                </div>
                {renderConsentPrompt(memory)}
              </div>
            );
          }

          /* ── Text card ── */
          return (
            <div key={memory.id} className={styles.masonryItem}>
              <div className={`${styles.textCard}${tap.cls}`} {...tap.props} style={{position:'relative', ...(tap.cls ? { cursor: 'pointer' } : {}), ...(memory.isOwn && memory.shareRequested ? { border: '2px dashed #838B70' } : {})}}>
                <EditOverlay memory={memory} onEdit={onEditMemory} />
                {responseHeader}
                {!respond && <p className={styles.textLabel}>TEXT</p>}   {/* v1.10: the response header's chip already says it */}

                <div className={styles.textContent}>
                  {memory.title && (
                    <p className={styles.textTitle}>{memory.title}</p>
                  )}
                  {memory.note && (
                    <p className={styles.textNote}>{memory.note}</p>
                  )}
                </div>

                <div className={styles.cardFooter}>
                  <span className={styles.cardDate}>{footerDate}</span>{footerEnd}
                </div>
              </div>
              {renderConsentPrompt(memory)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
